import { PROD_URL } from "assets/constants";
import { trackFormSubmission, trackDemoInteraction } from "utils/analytics";
import axios from "axios";
import { toast } from "react-toastify";

const dataServer = axios.create({
  baseURL: PROD_URL,
  timeout: 30000,
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
});

dataServer.interceptors.request.use(
  async (config) => {
    if (!navigator.onLine) {
      return Promise.reject({ message: "No internet connection" });
    }
    const accessToken =
      localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

dataServer.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if token is expired
    const isTokenExpired =
      error.response?.status === 401 &&
      error.response?.data?.message?.toLowerCase().includes("expired");

    // Token has expired: attempt refresh
    if (isTokenExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken =
        localStorage.getItem("refreshToken") || sessionStorage.getItem("refreshItem");

      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(`${PROD_URL}/Account/refresh-token`, {
            refreshToken,
          });

          const newAccessToken = refreshResponse.data.token;
          if (localStorage.getItem("role")) {
            localStorage.setItem("accessToken", newAccessToken);
          } else {
            sessionStorage.setItem("accessToken", newAccessToken);
          }

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return dataServer(originalRequest);
        } catch (refreshError) {
          localStorage.clear();
          sessionStorage.clear();
          // window.location.href = "/authentication/sign-in";
          return Promise.reject(refreshError);
        }
      } else {
        localStorage.clear();
        sessionStorage.clear();
        // window.location.href = "/authentication/sign-in";
        return Promise.reject(error);
      }
    }

    // Handle missing or invalid token (no token present)
    const isMissingToken =
      error.response?.status === 401 &&
      !localStorage.getItem("accessToken") &&
      !sessionStorage.getItem("accessToken");

    if (isMissingToken) {
      localStorage.clear();
      sessionStorage.clear();
      // window.location.href = "/authentication/sign-in";
      return Promise.reject(error);
    }

    // Otherwise, propagate the error
    return Promise.reject(error);
  }
);

export const callFrequency = async () => {
  try {
    const response = await dataServer.get(`${PROD_URL}CallSchedule/call-frequencies`);
    return response.data.data;
  } catch (error) {}
};

export const callTopics = async () => {
  try {
    const response = await dataServer.get(`${PROD_URL}CallSchedule/call-topics`);
    return response.data.data;
  } catch (error) {}
};

export const callTime = async () => {
  try {
    const response = await dataServer.get(`${PROD_URL}CallSchedule/call-times`);
    return response.data.data;
  } catch (error) {}
};

// ✅ Register API with GA tracking
export const register = async (req) => {
  const payload = {
    myEmail: req?.email,
    myName: req?.name,
    myMobilePhone: req?.phone,
    nameForCheckIn: req?.checkInName,
    relationship: req?.relationship,
    theirPhoneNumber: req?.theirPhone,
    callFrequencyOptionId: req?.callDay,
    callTimeOptionId: req?.callTime,
    TimeZoneId: req?.timeZone,
    consentToSms: req?.consentToSms,
  };

  const pendingToast = toast.loading("Processing your registration. Please wait...");

  try {
    const response = await dataServer.post(`${PROD_URL}Auth/register`, payload);

    // ✅ GA TRACKING — fire only on successful registration
    trackFormSubmission("signup", true);

    toast.update(pendingToast, {
      render: "Registration successful. Please check your email for your secure login link.",
      type: "success",
      isLoading: false,
      autoClose: 4000,
    });

    return response;
  } catch (error) {
    // ❌ GA TRACKING — fire on registration error
    //trackFormSubmission("signup", false);

    const backendMessage = error?.response?.data?.message;
    const finalMessage = backendMessage
      ? `We were unable to complete your registration: ${backendMessage}`
      : "We were unable to complete your registration. Please try again or contact support.";

    toast.update(pendingToast, {
      render: finalMessage,
      type: "error",
      isLoading: false,
      autoClose: 5000,
    });

    throw error;
  }
};
export const updateCallSettings = async (userId, callStatus) => {
  return toast.promise(
    dataServer.post(
      `${PROD_URL}CallSchedule/update-call-settings`,
      null, // No body, since you're using query params
      {
        params: {
          userId: userId,
          callStatus: callStatus,
        },
      }
    ),
    {
      pending: callStatus ? "Pausing your calls..." : "Resuming your calls...",
      success: callStatus ? "Calls have been paused." : "Calls have been resumed.",
      error: "Failed to update call settings. Please try again.",
    }
  );
};

export const userSettings = async (userId) => {
  try {
    console.log(userId);
    const response = await dataServer.get(`${PROD_URL}CallSchedule/settings?userId=${userId}`);
    console.log(response);
    return response.data.data;
  } catch (error) {}
};
export const update = async (req) => {
  return toast.promise(
    dataServer.post(`${PROD_URL}CallSchedule/update-settings`, {
      userId: req?.userId,
      callFrequencyOptionId: req?.callDay,
      callTimeOptionId: req?.callTime,
      timeZoneId: req?.timeZoneId,
      callFrequencyOption: req?.callFrequencyOption,
      userTopics: req?.userTopics,
    }),
    {
      pending: "⏳ Updating call settings...",
      success: {
        render({ data }) {
          return data?.data?.message || "✅ Settings updated successfully!";
        },
      },
      error: {
        render({ data }) {
          const msg = data?.response?.data || "❌ Update failed. Please try again.";
          return typeof msg === "string" ? msg : JSON.stringify(msg);
        },
      },
    }
  );
};
export const signout = async (req) => {
  try {
    const response = await dataServer.post(`${PROD_URL}Auth/logout`);

    // Handle successful request (status code 200)
    if (response.status === 200) {
      return { data: response.data, statusCode: response.status };
    }
  } catch (error) {
    console.log("errorfrom apiiiiii:", error);
    const status = error?.status;
    if (status === 400) {
      toast(error?.response.data);
    }
  }
};

// ✅ Demo Call API with GA tracking
export const demo = async ({ phoneNumber, name, email, topics }) => {
  const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;

  return toast.promise(
    dataServer.post(`${PROD_URL}demo/call`, {
      phoneNumber: formattedPhone,
      name: name?.trim(),
      email: email?.trim(),
      topicIds: topics || [], // send list of int IDs
    }),
    {
      pending: "📞 Initiating demo call...",
      success: {
        render({ data }) {
          // ✅ GA TRACKING — fire when demo call successfully starts
          trackDemoInteraction("demo_call_initiated", name || email || formattedPhone);
          return data?.data?.message || "✅ Call initiated successfully!";
        },
      },
      error: {
        render({ data }) {
          return data?.response?.data || "❌ Failed to initiate call. Please try again later.";
        },
      },
    }
  );
};
export const callHistory = async () => {
  try {
    const response = await dataServer.get(`${PROD_URL}user/calls`, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
    console.log(response);
    return response.data.data;
  } catch (error) {}
};

export const loginWithEmail = async (email) => {
  const toastId = toast.loading("Sending magic link to your email...");

  try {
    const response = await dataServer.post(`${PROD_URL}Auth/request-magic-link`, { email });

    toast.update(toastId, {
      render: "Login link sent successfully!",
      type: "success",
      isLoading: false,
      autoClose: 3000,
    });

    return response;
  } catch (error) {
    const status = error?.response?.status;
    let errorMessage = "Failed to send login link. Please try again.";

    if (status === 404) errorMessage = "Email not registered!";
    else if (status === 400) errorMessage = "Invalid email format.";

    toast.update(toastId, {
      render: errorMessage,
      type: "error",
      isLoading: false,
      autoClose: 4000,
    });

    throw error;
  }
};

export const resendEmailOTP = async (email) => {
  return toast.promise(axios.post(`${PROD_URL}Email/send-email`, { email }), {
    pending: "Sending verification code to email...",
    success: "Verification code sent successfully!",
    error: "Failed to resend verification code. Please try again.",
  });
};

export const resendPhoneOTP = async (phoneNumber) => {
  return toast.promise(
    axios.post(`${PROD_URL}SMS/SendVerificationCode`, JSON.stringify(phoneNumber), {
      headers: {
        "Content-Type": "application/json",
      },
    }),
    {
      pending: "Sending verification code to your Phone Number...",
      success: "Verification code sent successfully!",
      error: "Failed to resend verification code. Please try again.",
    }
  );
};
export const verifyEmailOTP = async (email, otp, newEmail = false) => {
  return toast.promise(axios.post(`${PROD_URL}Email/verify-email`, { email, otp, newEmail }), {
    pending: "Verifying email...",
    success: "Email verified successfully!",
    error:
      "The Email verification code you entered is invalid or has expired. Please request a new OTP and try again.",
  });
};
export const verifyPhoneOTP = async (email, phoneNumber, code, newPhoneNumber = false) => {
  return toast.promise(
    axios.post(`${PROD_URL}SMS/VerifyCode`, { email, phoneNumber, code, newPhoneNumber }),
    {
      pending: "Verifying phone...",
      success: "Phone number verified successfully!",
      error:
        "The Phone verification code you entered is invalid or has expired. Please request a new OTP and try again.",
    }
  );
};

export const getCurrentPlan = async () => {
  const token = localStorage.getItem("accessToken");

  const response = await axios.get(`${PROD_URL}Auth/GetUserCurrentPlane`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.data; // profile info
};

export const getUserProfile = async () => {
  const token = localStorage.getItem("accessToken");

  const response = await axios.get(`${PROD_URL}Auth/GetUserProfile`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.data; // returns ProfileDto object
};

export const updateUserProfile = async (payload) => {
  const token = localStorage.getItem("accessToken");

  return toast.promise(
    axios.post(`${PROD_URL}Auth/UpdateUserProfile`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    {
      pending: "Updating your profile...",
      success: "Profile updated successfully!",
      error: "Failed to update profile. Please try again!",
    }
  );
};

// const UpdateUserProfile = async (
//   profileData,
//   profileImageUrl,
//   companyLogoUrl,
//   documentUrls
// ) => {
//   try {
//     const formData = new FormData();
//     formData.append("name", profileData.name || "");
//     formData.append("email", profileData.email || "");
//     formData.append("phoneNumber", profileData.phoneNumber || "");
//     formData.append("businessName", profileData.businessName || "");
//     formData.append("personalWebsite", profileData.personalWebsite || "");
//     typeof profileData.servingStates === "string"
//       ? formData.append("servingStates", profileData.servingStates || [])
//       : formData.append(
//           "servingStates",
//           JSON.stringify(profileData.servingStates || [])
//         );
//     formData.append("numberOfYears", parseInt(profileData.noOfYears, 10) || 0);
//     formData.append("brokerName", profileData.brokerName || "");
//     formData.append("brokerContact", profileData.brokerContact || "");
//     formData.append("geographicalAreas", profileData.geographicalAreas || "");
//     formData.append("aboutMe", profileData.aboutMe || "");
//     formData.append("facebook", profileData.facebook || "");
//     formData.append("twitter", profileData.twitter || "");
//     formData.append("linkedIn", profileData.linkedIn || "");
//     formData.append("youtube", profileData.youtube || "");
//     formData.append("tikTok", profileData.tikTok || "");
//     formData.append("instagram", profileData.instagram || "");

//     formData.append(
//       "userProfileImage",
//       profileImageUrl || profileData?.userProfileImage || ""
//     );
//     formData.append(
//       "companyLogo",
//       companyLogoUrl || profileData?.companyLogo || ""
//     );
//     formData.append("market", profileData.market || []);
//     typeof profileData.language === "string"
//       ? formData.append("languages", profileData.language || [])
//       : formData.append(
//           "languages",
//           JSON.stringify(profileData.language || [])
//         );
//     const idTypes = Array.isArray(profileData?.governmentIssuedId)
//       ? profileData.governmentIssuedId
//       : profileData.governmentIssuedId
//       ? [profileData.governmentIssuedId]
//       : [];

//     idTypes.forEach((idType) => {
//       switch (idType) {
//         case "passports":
//           [
//             ...documentUrls,
//             ...(profileData.passports || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("passportUploads", file));
//           break;
//         case "driversLicenses":
//           [
//             ...documentUrls,
//             ...(profileData.driversLicenses || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("driversLicenseUploads", file));
//           break;
//         case "stateIDs":
//           [
//             ...documentUrls,
//             ...(profileData.stateIDs || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("stateIDUploads", file));
//           break;
//         case "militaryIds":
//           [
//             ...documentUrls,
//             ...(profileData.militaryIds || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("militaryIdUploads", file));
//           break;
//         case "greenCards":
//           [
//             ...documentUrls,
//             ...(profileData.greenCards || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("greenCardUploads", file));
//           break;
//         case "votersCards":
//           [
//             ...documentUrls,
//             ...(profileData.votersCards || []).filter(
//               (file) => file && file !== "null"
//             ),
//           ].forEach((file) => formData.append("votersCardUploads", file));
//           break;
//         default:
//           break;
//       }
//     });

//     console.log("Form Data:", formData);

//     // Send the POST request with FormData
//     const response = await dataServer.post(
//       `${PROD_URL}/SellerProfile/UpdateSellerProfileById?id=${profileData?.userId}`,
//       formData
//       // { headers: { "Content-Type": "multipart/form-data" } }
//     );

//     console.log("Response Status:", response.status);
//     return response.data;
//   } catch (error) {
//     const statusCode = error.response ? error.response.status : null;
//     console.log("Error:", statusCode);

//     if (statusCode === 500) {
//       if (
//         error.response?.details ===
//         "The phone number is already associated with another user."
//       ) {
//         return statusCode;
//       }
//     }
//     if (statusCode === 400) {
//       console.error("Profile changes failed. Something went wrong.");
//     } else {
//       console.error(`An error occurred: ${error}`);
//     }

//     throw error;
//   }
// };

export const subscribeEmails = async (token, emailStatus) => {
  const response = await dataServer.get(
    `${PROD_URL}UserDistributionEmail/subscribe?token=${token}&emailStatus=${emailStatus}`
  );
  console.log(response);
  return response;
};

export const unSubscribeEmails = async (token, emailStatus) => {
  const response = await dataServer.get(
    `${PROD_URL}UserDistributionEmail/unsubscribe?token=${token}&emailStatus=${emailStatus}`
  );
  console.log(response);
  return response;
};

export const updatePauseStatus = async (payload) => {
  const token = localStorage.getItem("accessToken");
  return toast.promise(
    axios.post(`${PROD_URL}UserDistributionEmail/updatePauseStatus`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    }),
    {
      pending: "Updating email status...",
      success: "Status updated successfully!",
      error: "Failed to update status. Please try again!",
    }
  );
};

// api/distributionEmails.js
export const sendPermissionMail = async ({ userId, email }) => {
  const token = localStorage.getItem("accessToken");
  return toast.promise(
    axios.post(
      `${PROD_URL}UserDistributionEmail/sendPermissionMail`,
      null, // POST with no body
      {
        params: { userId, email }, // FromQuery
        headers: { Authorization: `Bearer ${token}` },
      }
    ),
    {
      pending: "Sending invite...",
      success: "Invite sent!",
      error: "Failed to send invite. Please try again!",
    }
  );
};

export const deleteDistributor = async ({ userId, email }) => {
  const token = localStorage.getItem("accessToken");
  return toast.promise(
    axios.post(
      `${PROD_URL}UserDistributionEmail/deleteDistributor`,
      null, // no body, only query params
      {
        params: { userId, email },
        headers: { Authorization: `Bearer ${token}` },
      }
    ),
    {
      pending: "Removing recipient...",
      success: "Recipient removed!",
      error: "Failed to remove recipient. Please try again!",
    }
  );
};

export const fetchDashboardData = async (userId, days = 7) => {
  var endpoint = `${PROD_URL}reports/dashboard`; // for dev and staging
  if (PROD_URL.includes("core")) {
    endpoint = `${PROD_URL}reports/dashboardv2`; // for production
  }
  const token = localStorage.getItem("accessToken");
  const response = await axios.get(`${endpoint}`, {
    params: { userId, days },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data; // ApiResponse<List<T>> wraps inside `.data`
};

export const fetchDataInsights = async (userId, days = 7) => {
  var endpoint = `${PROD_URL}reports/get-trend-insights`;
  const token = localStorage.getItem("accessToken");
  const response = await axios.get(`${endpoint}`, {
    params: { userId, days },
    headers: { Authorization: `Bearer ${token}` },
  });
  let raw = response.data?.data || "";

  // 🧹 Clean any leading "json" or code fences (like ```json or json { ... })
  raw = raw
    .replace(/^```json/i, "") // remove leading ```json
    .replace(/^json\s*/i, "") // remove starting "json " prefix
    .replace(/```$/i, "") // remove trailing ```
    .trim();

  if (!raw || raw === "{}") return null;

  const parsed = JSON.parse(raw);
  return parsed;
};

// ✅ Get Primary Agent (GET)
export const getPrimaryAgent = async () => {
  try {
    const res = await dataServer.get(`${PROD_URL}voice/get-primary-agent`);
    return res?.data;
  } catch (error) {
    console.error("❌ Failed to fetch primary agent:", error);
    throw error.response?.data || "Failed to fetch primary agent";
  }
};

export const setPrimaryAgent = async ({ agentType, provider, voiceId }) => {
  try {
    const query = new URLSearchParams({
      agentType,
      ...(provider ? { provider } : {}),
      ...(voiceId ? { voiceId } : {}),
    }).toString();

    const res = await dataServer.post(`${PROD_URL}voice/set-primary-agent?${query}`);
    return res?.data;
  } catch (error) {
    console.error("❌ Failed to set primary agent:", error);
    throw error.response?.data || "Failed to set primary agent";
  }
};

export const getAgentHistory = async () => {
  try {
    const res = await dataServer.get(`${PROD_URL}voice/agent-history`);
    return res?.data;
  } catch (error) {
    console.error("❌ Failed to fetch agent history:", error);
    throw error.response?.data || "Failed to fetch agent history";
  }
};
// ✅ Start Call API (POST multipart/form-data)
export const startCall = async ({ email, phoneNumber, name, followUp, topics, primaryAgent }) => {
  const formattedPhone = phoneNumber?.startsWith("+") ? phoneNumber : `+${phoneNumber}`;

  const formData = new FormData();
  formData.append("Email", email?.trim());
  formData.append("PhoneNumber", formattedPhone);
  formData.append("Name", name?.trim());
  formData.append("FollowUp", followUp ? "true" : "false");
  formData.append("PrimaryAgent", primaryAgent || "ElevenLabs");

  // Assuming your backend DTO maps Topic1, Topic2, Topic3
  topics?.slice(0, 3).forEach((t, i) => {
    formData.append(`Topic${i + 1}`, t);
  });

  return toast.promise(
    dataServer.post(`${PROD_URL}voice/start-call`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
    {
      pending: "📞 Starting call...",
      success: {
        render({ data }) {
          return data?.data?.message || "✅ Call started successfully!";
        },
      },
      error: {
        render({ data }) {
          return data?.response?.data || "❌ Failed to start call. Please try again later.";
        },
      },
    }
  );
};

// ✅ Fetch all voices from API
export const getPhonelyProviders = async () => {
  try {
    const res = await axios.get(`${PROD_URL}voice/phonely-providers`);
    return res?.data || [];
  } catch (error) {
    console.error("❌ Failed to fetch voice list:", error);
    throw error.response?.data || "Failed to fetch voice list";
  }
};
